import { useState, useMemo, useEffect } from "react";
import { Button, Form, Input, notification, Upload, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { get, put, uploadFile } from "../../services";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import { ReactQuillOptions } from "../../utils/react-quill-options";
import { generateSlug2 as generateSlug } from "../../utils/generate-slug";

const EventForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [getLoader, setGetLoader] = useState(true);
  const [fileList2, setFileList2] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [information, setInformation] = useState({});
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  // Notification handler
  const openNotification = (message, description) => {
    notification.open({
      message,
      description,
    });
  };

  // useEffect to get content
  useEffect(() => {
    get(`/blogs/${id}`)
      .then((response) => {
        console.log("response?.data", response?.data);
        setSlug(response?.data?.blog.slug);
        setTitle(response?.data?.blog.title);
        setInformation({
          ...response?.data?.blog,
          meta_description:
            response?.data?.blog?.metaData?.description,
          meta_title: response?.data?.blog?.metaData?.title,
          meta_keywords: response?.data?.blog?.metaData?.keywords,
        });
        setGetLoader(false);
      })
      .catch((error) => {
        setLoader(false);
      });
    // eslint-disable-next-line
  }, []);

  // Props for single image upload
  const uploadProps2 = useMemo(
    () => ({
      beforeUpload: (file) => {
        const isImage = file?.type?.split("/")[0] === "image";
        const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
        const hasSpacesInName = /\s/.test(file.name); // check for any whitespace

        if (!isImage) {
          openNotification("Error", "Please upload an image file.");
          return false;
        }
        if (!isValidSize) {
          openNotification("Error", "File size exceeds 5MB limit.");
          return false; // Prevent upload if file size exceeds 5MB
        }
        if (hasSpacesInName) {
          openNotification(
            "Error",
            "Remove spaces from file name before uploading."
          );
          return false;
        }
        setFileList2([file]);
        return false; // Prevent automatic upload
      },
      onRemove: () => setFileList2([]),
    }),
    []
  );

  // Function to upload files and get their URLs
  let uploadInProgress = false; // Track whether an upload is in progress
  const uploadFileHandler = async (file) => {
    // Prevent multiple uploads while one is in progress
    if (uploadInProgress) {
      openNotification(
        "Notification",
        "File is already uploading. Please wait."
      );
      return null; // Don't proceed with upload if another one is in progress
    }
    uploadInProgress = true; // Set flag to prevent further uploads
    try {
      const response = await uploadFile("/getSignUrlForUpload", { file: file });
      if (response?.data?.publicUrl) {
        return response.data.publicUrl;
      } else {
        openNotification("Error", "File upload failed.");
        return null;
      }
    } catch (error) {
      openNotification("Error", error?.message || "Something went wrong.");
      return null;
    } finally {
      // Reset the flag once the upload process is finished (success or failure)
      uploadInProgress = false;
    }
  };

  // Form submission handler
  const onFinish = async (values) => {
    if (isUploading) {
      openNotification(
        "Notification",
        "File is already uploading. Please wait."
      );
      return false; // Prevent form submission if upload is in progress
    }

    // Set the uploading flag to true
    setIsUploading(true);
    setLoader(true);

    try {
      let uploadedFilesUrl;
      if (fileList2.length > 0) {
        openNotification(
          "Notification",
          "Please wait, file upload may take some time."
        );
        const fileToUpload = fileList2.length > 0 ? fileList2[0] : [];
        // Upload the file using the helper function
        uploadedFilesUrl = await uploadFileHandler(fileToUpload);
        if (!uploadedFilesUrl) {
          openNotification("Error", "File upload failed.");
          setLoader(false);
          setIsUploading(false);
        }
      }
      // Construct payload for the API request
      let payload = {
        slug: slug === "" ? generateSlug(values?.title || "") : slug,
        title: values?.title,
        domain: "Empire",
        description: values?.description ? values?.description : "null",
        date: values?.date ? values?.date : "null",
        images: [
          {
            url: information?.images[0]?.url,
            // metaData: {
            //   description: values?.meta_description,
            //   title: values?.meta_title,
            //   keywords: values?.meta_keywords,
            // },
          },
        ],
        metaData: {
          description: values?.meta_description,
          title: values?.meta_title,
          keywords: values?.meta_keywords,
        },
      };
      // updated image
      if (uploadedFilesUrl && fileList2.length !== 0) {
        payload = {
          ...payload,
          images: [
            {
              url: uploadedFilesUrl,
              metaData: {
                description: values?.meta_description,
                title: values?.meta_title,
                keywords: values?.meta_keywords,
              },
            },
          ],
        };
      }
      // Make the API call to post the data
      await put(`/blogs/${id}`, payload);
      // Success handling
      setLoader(false);
      setIsUploading(false); // Reset the flag after successful upload
      navigate("/blogs");
      openNotification("Success", "Record updated successfully.");
    } catch (error) {
      setLoader(false);
      setIsUploading(false); // Reset the flag in case of error
      openNotification("Error", error?.message || "Something went wrong.");
    }
  };

  return (
    <>
      <div className="header">
        <h3>Edit News & Blog</h3>
      </div>
      {!getLoader ? (
        <div className="form_container">
          <Form
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              title: information?.title,
              date: information?.date,
              description: information?.description,
              meta_description: information?.meta_description,
              meta_title: information?.meta_title,
              meta_keywords: information?.meta_keywords,
            }}
          >
            <Form.Item
              name="image"
              label=""
              rules={[{ required: false, message: "" }]}
            >
              <Upload
                name="image"
                listType="picture"
                {...uploadProps2}
                fileList={fileList2}
              >
                <Button icon={<UploadOutlined />}>Upload image</Button>
              </Upload>
            </Form.Item>

            {/* Title Input */}
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Please enter the title." }]}
            >
              <Input />
            </Form.Item>
            {/* Custiom Slug */}
            <div class="ant-form-item css-dev-only-do-not-override-1b0bdye">
              <div class="ant-row ant-form-item-row css-dev-only-do-not-override-1b0bdye">
                <div class="ant-col ant-form-item-label css-dev-only-do-not-override-1b0bdye">
                  <label>Slug</label>
                </div>
                <div className="input-button-slug-container">
                  <div className="input-container">
                    <input
                      class="ant-custom-input ant-custom-input"
                      type="text"
                      value={slug}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSlug(generateSlug(value));
                        setIsSlugEdited(true);
                      }}
                    />
                  </div>
                  <div>
                    <Button
                      disabled={!isSlugEdited}
                      onClick={() => {
                        // Reset slug if empty
                        const fallbackSlug = generateSlug(title || "");
                        setSlug(fallbackSlug);
                        setIsSlugEdited(false);
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {/* Title Input */}
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: false, message: "Please select your date." }]}
            >
              <Input type="date" />
            </Form.Item>

            {/* description */}
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: false, message: "Description is required" }]}
            >
              <ReactQuill theme="snow" modules={ReactQuillOptions} />
            </Form.Item>
            {/* Meta Title*/}
            <Form.Item
              name="meta_title"
              label="Meta Title"
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Input />
            </Form.Item>
            {/* meta_keywords */}
            <Form.Item
              name="meta_keywords"
              label="Meta Keywords"
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Input />
            </Form.Item>
            {/* description */}
            <Form.Item
              name="meta_description"
              label="Meta Description"
              rules={[{ required: false, message: "Description is required" }]}
            >
              <ReactQuill theme="snow" modules={ReactQuillOptions} />
            </Form.Item>

            {/* Submit Buttons */}
            <Form.Item>
              <div className="form_submit">
                <Button
                  className="back_button"
                  onClick={() => navigate("/blogs")}
                >
                  Back
                </Button>
                <Button htmlType="submit" disabled={loader}>
                  {loader ? "Uploading..." : "Save"}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      ) : (
        <div className="spin_information">
          <Spin />
        </div>
      )}
    </>
  );
};

export default EventForm;
